import { FlowContext } from 'src/lib/flow';
import {
  createExperimentPage,
  disabledPage,
  experimentPage,
  IsEligibleForExperimentFn,
} from 'src/lib/flow/experimentPage';
import { composePage, FlowPage } from 'src/lib/flow/FlowPage';
import { addPageLoader, FlowPageLoader } from 'src/lib/flow/loaders';
import { isAgent } from 'src/lib/flow/pageHelpers';
import { loadExperiment } from 'src/store/apiUtils';
import { getFlowFromState } from 'src/store/flow_listeners';

import accountPage from './accountPage';

const eligibleWhenEnabled =
  (isEnabled: FlowPage['isEnabled']): IsEligibleForExperimentFn =>
  (listenerApi) => {
    const { context } = getFlowFromState(listenerApi.getState());
    return isEnabled(context);
  };

const eligibleForShortboarding = ({
  answers,
  inputContext: { channel, homeValuationEstimate },
  featureFlags,
}: FlowContext) => {
  // no agents
  if (isAgent(answers, channel)) {
    return false;
  }

  // no list with certainty users
  if (featureFlags.isInListWithCertaintyTreatment) {
    return false;
  }

  // include only homes that have a valuation
  if (homeValuationEstimate && homeValuationEstimate.variance < 0.1) {
    return true;
  }

  return false;
};

const selectedShortboarding = (context: FlowContext) =>
  eligibleForShortboarding(context) && context.answers['seller.sale_timeline'] === 'Just Browsing';

// Since we don't know if a user will actually get the shortboarding experience
// we've set this up as a two flag test. The first flag will display the
// sale-timeline early in the flow, and the second experiment will load for
// users who are in that test and have answered  "Just Browsing", this should
// help to get useful stats in amplitude
//
// The following pages will manually set up the experimentPage and use this loader
const loadShortboardingExperiment: FlowPageLoader = async (listenerApi) => {
  //check if user selected "Just Browsing" and has a home valuation
  if (!eligibleWhenEnabled(selectedShortboarding)(listenerApi)) {
    return;
  }

  // if user is in shortboardingSaleTimeline then also bucket them for shortboarding
  await loadExperiment(listenerApi, 'shortboarding', true);
};

const account = experimentPage('shortboarding', {
  control: disabledPage('account-sb'),
  treatment: composePage(accountPage, {
    isEnabled: selectedShortboarding,
    slug: () => 'account-sb',
  }),
});

addPageLoader('account-sb', 'load-experiment', loadShortboardingExperiment);

// add offer-estimate page
const offerEstimate: FlowPage = {
  slug: () => 'offer-estimate',
  subtitleElement: ({ inputContext: { homeValuationEstimate } }) => {
    if (homeValuationEstimate) {
      return {
        type: 'element',
        key: 'offer_range',
        component: 'offer_range',
        props: {
          min: homeValuationEstimate.min,
          max: homeValuationEstimate.max,
        },
      };
    }
  },
  isEnabled: (ctx) => selectedShortboarding(ctx),
  autoAdvance: () => false,
  questions: () => [
    {
      key: 'selling_options',
      type: 'element',
      component: 'selling_options',
    },
  ],
  nextText: () => 'Get an offer',
  secondaryAction: () => ({
    label: 'Track home value',
    answerSet: [{ key: 'offer_estimate.track', answer: true }],
  }),
};

const offerEstimatePage = experimentPage('shortboarding', {
  control: disabledPage('offer-estimate'),
  treatment: offerEstimate,
});

addPageLoader('offer-estimate', 'load-experiment', loadShortboardingExperiment);

// add offer-tracking page
// todo: with service is set to reception we do not get resizing but still get an image, so use cosmos for now
const offerTracking: FlowPage = {
  slug: () => 'offer-tracking',
  title: () => "You're tracking your estimated offer range",
  subtitle: () =>
    "We'll provide regular updates on how much we think you could sell your home for. We can share a cash offer when you add more details about your home.",
  isEnabled: (ctx) => {
    return ctx.answers['offer_estimate.track'] == true && selectedShortboarding(ctx);
  },
  autoAdvance: () => false,
  questions: () => [
    {
      key: 'offer_illustration',
      type: 'element',
      component: 'image',
      props: {
        src: 'https://images.opendoor.com/source/s3/imgdrop-production/2024-03-1711579928976-27493.jpg?preset=square-512&service=cosmos&dpr=2',
        alt: 'An illustration of an offer inside an open envelope.',
        width: '100%',
      },
    },
  ],
  nextText: () => 'Get an offer',
};

const offerTrackingPage = experimentPage('shortboarding', {
  control: disabledPage('offer-tracking'),
  treatment: offerTracking,
});

addPageLoader('offer-tracking', 'load-experiment', loadShortboardingExperiment);

// disable sale timeline and account page further in the flow
export const pageSet = [account, offerEstimatePage, offerTrackingPage];

export const accountPageExcludeShortboarding = createExperimentPage(
  'shortboarding',
  {
    control: accountPage,
    treatment: disabledPage('account'),
  },
  eligibleWhenEnabled((ctx) => {
    return selectedShortboarding(ctx) && typeof ctx.answers['seller.email'] !== 'undefined';
  }),
);
